import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Paper, Typography, TextField, Button, IconButton,
  CircularProgress, Alert, Divider, LinearProgress, Chip,
  Accordion, AccordionSummary, AccordionDetails, Avatar, Tooltip
} from '@mui/material';
import {
  ManageAccounts, ExpandMore, Lock, Email,
  HowToVote, CalendarToday, EmojiPeople, Security, Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

const VOTE_TIERS = [
  { name: 'Nomade Temporel',      emoji: '⏳', min: 0,   max: 19,  ogrines: 100 },
  { name: 'Voyageur des Failles', emoji: '🌀', min: 20,  max: 59,  ogrines: 100 },
  { name: 'Fractureur',           emoji: '⚡', min: 60,  max: 129, ogrines: 100 },
  { name: 'Gardien Nexus',       emoji: '🔮', min: 130, max: 219, ogrines: 100 },
  { name: 'Seigneur du Nexus',    emoji: '👑', min: 220, max: null, ogrines: 100 },
];

function getCurrentTier(monthlyVotes) {
  for (let i = VOTE_TIERS.length - 1; i >= 0; i--) {
    if (monthlyVotes >= VOTE_TIERS[i].min) return VOTE_TIERS[i];
  }
  return VOTE_TIERS[0];
}

const fieldSx = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(182, 122, 60, 0.3)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(182, 122, 60, 0.5)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#B67A3C' },
};

const btnSx = {
  background: 'linear-gradient(135deg, #B67A3C 0%, #A7AEB8 100%)',
  fontWeight: 700,
  '&:hover': { background: 'linear-gradient(135deg, #8D5A2A 0%, #8D5A2A 100%)', transform: 'translateY(-1px)' },
  '&:disabled': { background: 'rgba(182, 122, 60, 0.3)', color: 'rgba(255,255,255,0.4)' },
};

const sectionPaper = {
  padding: 3,
  background: 'rgba(26, 10, 46, 0.7)',
  border: '1px solid rgba(182, 122, 60, 0.2)',
  borderRadius: '16px',
  marginBottom: 3,
};

const LEGACY_COFFRE_REWARD_ALIASES = {
  'panoplie tooshuss': 'Panoplie Tooschuss',
  'panoplie tokageko': 'Panoplie Subtile',
  'panoplie trappeur': 'Panoplie Loterie Turquoise',
  'panoplie lary': 'Panoplie Kitsoune',
};

const DELIVERY_BRIDGE_ENABLED = true;

function normalizeRewardName(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

function getCanonicalRewardName(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return LEGACY_COFFRE_REWARD_ALIASES[normalizeRewardName(raw)] || raw;
}

export default function MonCompte() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [passForm, setPassForm] = useState({ current_password: '', new_password: '', confirm_password: '', secret_answer: '' });
  const [passMsg, setPassMsg] = useState(null);
  const [passLoading, setPassLoading] = useState(false);

  const [emailForm, setEmailForm] = useState({ new_email: '', password: '', secret_answer: '' });
  const [emailMsg, setEmailMsg] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  const [pseudoForm, setPseudoForm] = useState({ pseudo: '', current_password: '' });
  const [pseudoMsg, setPseudoMsg] = useState(null);
  const [pseudoLoading, setPseudoLoading] = useState(false);

  const [whitelistIps, setWhitelistIps] = useState([]);
  const [whitelistLoading, setWhitelistLoading] = useState(false);
  const [whitelistMsg, setWhitelistMsg] = useState(null);
  const [ipReqLoading, setIpReqLoading] = useState(false);
  const [ipReqMsg, setIpReqMsg] = useState(null);

  const [deliveryInventory, setDeliveryInventory] = useState([]);
  const [deliveryCharacters, setDeliveryCharacters] = useState([]);
  const [selectedDeliveryPlayerId, setSelectedDeliveryPlayerId] = useState('');
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [sendingDeliveryId, setSendingDeliveryId] = useState(null);
  const [deliveryMsg, setDeliveryMsg] = useState(null);

  const hasOnlineDeliveryCharacter = deliveryCharacters.some((c) => Boolean(c.online));
  const selectedDeliveryCharacter = deliveryCharacters.find((c) => String(c.id) === String(selectedDeliveryPlayerId));
  const canSendDeliveryToGame = DELIVERY_BRIDGE_ENABLED
    && (selectedDeliveryPlayerId ? Boolean(selectedDeliveryCharacter?.online) : hasOnlineDeliveryCharacter);

  const loadDeliveryInventory = useCallback(async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      setDeliveryInventory([]);
      setDeliveryCharacters([]);
      setSelectedDeliveryPlayerId('');
      return;
    }

    setDeliveryLoading(true);
    setDeliveryMsg(null);
    try {
      const [invRes, charsRes] = await Promise.all([
        fetch(`${API_URL}/token-wheel/inventory`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/token-wheel/characters`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const invData = await invRes.json().catch(() => ({}));
      const charsData = await charsRes.json().catch(() => ({}));

      if (!invRes.ok || !invData.success) {
        throw new Error(invData.message || 'Impossible de charger les gains.');
      }
      const nextInventory = Array.isArray(invData.inventory) ? invData.inventory : [];
      setDeliveryInventory(
        nextInventory.map((row) => ({
          ...row,
          itemName: getCanonicalRewardName(row?.itemName || row?.item_name),
        }))
      );

      if (charsRes.ok && charsData.success) {
        const chars = Array.isArray(charsData.characters) ? charsData.characters : [];
        setDeliveryCharacters(chars);
        const defaultId = charsData.defaultPlayerId ? String(charsData.defaultPlayerId) : '';
        setSelectedDeliveryPlayerId(prev => {
          if (prev && chars.some(c => String(c.id) === String(prev))) {
            return prev;
          }
          return defaultId;
        });
      }
    } catch (err) {
      setDeliveryMsg({ type: 'error', text: err?.message || 'Erreur réseau lors du chargement des gains.' });
    } finally {
      setDeliveryLoading(false);
    }
  }, []);

  const sendDeliveryToGame = useCallback(async (deliveryId) => {
    if (!DELIVERY_BRIDGE_ENABLED) {
      setDeliveryMsg({ type: 'info', text: 'Reboot serveur attendu' });
      return;
    }

    const hasOnlineCharacter = deliveryCharacters.some((c) => Boolean(c.online));
    const selectedCharacter = deliveryCharacters.find((c) => String(c.id) === String(selectedDeliveryPlayerId));
    const selectedCharacterOnline = selectedDeliveryPlayerId
      ? Boolean(selectedCharacter?.online)
      : hasOnlineCharacter;

    if (!selectedCharacterOnline) {
      setDeliveryMsg({
        type: 'warning',
        text: 'Impossible d envoyer: vous devez selectionner un personnage en ligne.',
      });
      return;
    }

    const token = localStorage.getItem('sessionToken');
    if (!token || sendingDeliveryId) return;

    setSendingDeliveryId(deliveryId);
    setDeliveryMsg(null);
    try {
      const payload = { deliveryId };
      if (selectedDeliveryPlayerId) {
        payload.playerId = Number(selectedDeliveryPlayerId);
      }

      const res = await fetch(`${API_URL}/token-wheel/send-to-game`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        const backendMessage = String(data?.message || '').trim();
        const backendCode = String(data?.code || '').trim();
        const text = backendMessage || "Echec de l'envoi, contacter un administrateur";
        setDeliveryMsg({ type: 'error', text: backendCode ? `${text} (${backendCode})` : text });
        return;
      }

      if (data.delivery) {
        setDeliveryInventory(prev => prev.filter(row => Number(row.id) !== Number(deliveryId)));
      }

      if (data.delivered) {
        setDeliveryMsg({ type: 'success', text: 'Recompense envoyee en jeu.' });
      } else {
        const backendMessage = String(data?.message || '').trim();
        const backendCode = String(data?.code || '').trim();
        const text = backendMessage || "Echec de l'envoi, contacter un administrateur";
        setDeliveryMsg({ type: 'error', text: backendCode ? `${text} (${backendCode})` : text });
      }
    } catch {
      setDeliveryMsg({ type: 'error', text: 'Erreur reseau lors de l\'envoi en jeu.' });
    } finally {
      setSendingDeliveryId(null);
    }
  }, [deliveryCharacters, selectedDeliveryPlayerId, sendingDeliveryId]);

  const loadWhitelistIps = useCallback(async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) return;
    setWhitelistLoading(true);
    setWhitelistMsg(null);
    try {
      const res = await fetch(`${API_URL}/account/whitelist`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setWhitelistIps(data.ips || []);
      } else {
        setWhitelistMsg({ type: 'error', text: 'Impossible de charger les IPs.' });
      }
    } catch { setWhitelistMsg({ type: 'error', text: 'Erreur réseau.' }); }
    finally { setWhitelistLoading(false); }
  }, []);

  const handleDeleteIp = async (ipId) => {
    const token = localStorage.getItem('sessionToken');
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/account/whitelist/${ipId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        setWhitelistIps(prev => prev.filter(e => e.id !== ipId));
      } else {
        const data = await res.json();
        setWhitelistMsg({ type: 'error', text: data.error || 'Erreur lors de la suppression.' });
      }
    } catch { setWhitelistMsg({ type: 'error', text: 'Erreur réseau.' }); }
  };

  const handleRequestIpValidation = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) return;
    setIpReqLoading(true);
    setIpReqMsg(null);
    try {
      const res = await fetch(`${API_URL}/account/request-ip-validation`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setIpReqMsg({ type: 'error', text: data.error || 'Erreur serveur.' });
      } else if (data.validated_pending) {
        setIpReqMsg({ type: 'success', text: `✅ IPs validées : ${data.ips.join(', ')}. Vous pouvez maintenant accéder au jeu.` });
        loadWhitelistIps();
      } else if (data.already_validated) {
        setIpReqMsg({ type: 'success', text: `✅ Vos IPs sont déjà validées : ${data.ips.join(', ')}` });
      } else if (data.auto_validated) {
        setIpReqMsg({ type: 'success', text: `✅ IPs validées automatiquement : ${data.ips.join(', ')}` });
        loadWhitelistIps();
      } else if (data.email_sent) {
        setIpReqMsg({ type: 'info', text: `📧 Email envoyé à ${data.masked_email} pour valider : ${data.ips.join(', ')}. Cliquez sur le lien reçu pour accéder au jeu.` });
      }
    } catch { setIpReqMsg({ type: 'error', text: 'Erreur réseau.' }); }
    finally { setIpReqLoading(false); }
  };

  const loadProfile = useCallback(async (guid) => {
    const token = localStorage.getItem('sessionToken');
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/account/profile/${guid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setProfile(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/login'); return; }
    const u = JSON.parse(stored);
    setUser(u);
    loadProfile(u.id).finally(() => setLoading(false));
    loadDeliveryInventory();
  }, [navigate, loadProfile, loadDeliveryInventory]);

  const handlePassChange = (e) => setPassForm({ ...passForm, [e.target.name]: e.target.value });
  const handleEmailChange = (e) => setEmailForm({ ...emailForm, [e.target.name]: e.target.value });
  const handlePseudoChange = (e) => setPseudoForm({ ...pseudoForm, [e.target.name]: e.target.value });

  const submitPassword = async (e) => {
    e.preventDefault();
    setPassMsg(null);
    if (passForm.new_password !== passForm.confirm_password) {
      setPassMsg({ type: 'error', text: 'Les mots de passe ne correspondent pas.' });
      return;
    }
    if (passForm.new_password.length < 6) {
      setPassMsg({ type: 'error', text: 'Le nouveau mot de passe doit faire au moins 6 caractères.' });
      return;
    }
    setPassLoading(true);
    try {
      const res = await fetch(`${API_URL}/account/changePassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: user.id, ...passForm }),
      });
      const data = await res.json();
      setPassMsg({ type: res.ok ? 'success' : 'error', text: res.ok ? data.message : data.error });
      if (res.ok) setPassForm({ current_password: '', new_password: '', confirm_password: '', secret_answer: '' });
    } catch { setPassMsg({ type: 'error', text: 'Erreur réseau.' }); }
    finally { setPassLoading(false); }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailMsg(null);
    setEmailLoading(true);
    try {
      const res = await fetch(`${API_URL}/account/changeEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: user.id, ...emailForm }),
      });
      const data = await res.json();
      setEmailMsg({ type: res.ok ? 'success' : 'error', text: res.ok ? data.message : data.error });
      if (res.ok) {
        setEmailForm({ new_email: '', password: '', secret_answer: '' });
        const updated = { ...user, email: emailForm.new_email };
        localStorage.setItem('user', JSON.stringify(updated));
        setUser(updated);
      }
    } catch { setEmailMsg({ type: 'error', text: 'Erreur réseau.' }); }
    finally { setEmailLoading(false); }
  };

  const submitPseudo = async (e) => {
    e.preventDefault();
    setPseudoMsg(null);
    if (!pseudoForm.pseudo.trim()) {
      setPseudoMsg({ type: 'error', text: 'Le pseudo ne peut pas être vide.' });
      return;
    }
    if (!pseudoForm.current_password) {
      setPseudoMsg({ type: 'error', text: 'Le mot de passe est obligatoire.' });
      return;
    }
    setPseudoLoading(true);
    try {
      const res = await fetch(`${API_URL}/account/setPseudo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: user.id, pseudo: pseudoForm.pseudo, current_password: pseudoForm.current_password }),
      });
      const data = await res.json();
      if (res.ok) {
        setPseudoMsg({ type: 'success', text: data.message });
        const updated = { ...user, pseudo: data.pseudo };
        localStorage.setItem('user', JSON.stringify(updated));
        setUser(updated);
        await loadProfile(user.id);
        setPseudoForm({ pseudo: '', current_password: '' });
      } else {
        setPseudoMsg({ type: 'error', text: data.error });
      }
    } catch { setPseudoMsg({ type: 'error', text: 'Erreur réseau.' }); }
    finally { setPseudoLoading(false); }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: '#B67A3C' }} />
      </Box>
    );
  }

  const tier = profile ? getCurrentTier(profile.monthly_votes) : null;
  const nextTier = tier ? VOTE_TIERS[VOTE_TIERS.indexOf(tier) + 1] : null;
  const progress = tier && nextTier
    ? Math.round(((profile.monthly_votes - tier.min) / (nextTier.min - tier.min)) * 100)
    : 100;

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a0015 0%, #14192d 50%, #1a0a2e 100%)', paddingTop: '80px', paddingBottom: '60px' }}>
      <Container maxWidth="md">

        {/* En-tête */}
        <Box sx={{ textAlign: 'center', marginBottom: 4 }}>
          <Avatar sx={{ width: 80, height: 80, background: 'linear-gradient(135deg, #B67A3C, #A7AEB8)', margin: '0 auto 16px', fontSize: 36 }}>
            {user?.username?.[0]?.toUpperCase() || '?'}
          </Avatar>
          <Typography variant="h4" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #B67A3C, #E4E7EC, #C9CED6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Mon Compte
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)', mt: 1 }}>
            {user?.username}
          </Typography>
        </Box>

        {/* Définir pseudo (si pas encore défini) — PRIORITAIRE, tout en haut */}
        {profile && !profile.pseudo && (
          <Paper elevation={4} sx={{ ...sectionPaper, border: '2px solid rgba(255, 193, 7, 0.6)', background: 'rgba(26, 10, 46, 0.9)', marginBottom: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
              <EmojiPeople sx={{ color: '#C9CED6' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#C9CED6' }}>⚠️ Définir votre pseudo</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', marginBottom: 2 }}>
              Votre pseudo sera affiché publiquement dans le classement des votes à la place de votre nom de compte.
              Il ne pourra plus être modifié après validation.
            </Typography>
            {pseudoMsg && <Alert severity={pseudoMsg.type} sx={{ marginBottom: 2, borderRadius: 2 }}>{pseudoMsg.text}</Alert>}
            <form onSubmit={submitPseudo}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                  label="Votre pseudo"
                  name="pseudo"
                  value={pseudoForm.pseudo}
                  onChange={handlePseudoChange}
                  size="small"
                  required
                  helperText="3-20 caractères — visible publiquement, non modifiable"
                  FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.35)' } }}
                  sx={{ flexGrow: 1, minWidth: 180 }}
                  InputProps={{ sx: { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 193, 7, 0.4)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 193, 7, 0.7)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#C9CED6' } } }}
                />
                <TextField
                  label="Mot de passe actuel"
                  name="current_password"
                  type="password"
                  value={pseudoForm.current_password}
                  onChange={handlePseudoChange}
                  size="small"
                  required
                  helperText="Pour confirmer que c'est votre compte"
                  FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.35)' } }}
                  sx={{ flexGrow: 1, minWidth: 180 }}
                  InputProps={{ sx: { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 193, 7, 0.4)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 193, 7, 0.7)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#C9CED6' } } }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={pseudoLoading}
                  sx={{ background: 'linear-gradient(135deg, #F57F17 0%, #C9CED6 100%)', color: '#000', fontWeight: 700, whiteSpace: 'nowrap', '&:hover': { background: 'linear-gradient(135deg, #E65100 0%, #A56D33 100%)' }, '&:disabled': { background: 'rgba(255, 193, 7, 0.3)', color: 'rgba(0,0,0,0.4)' } }}
                >
                  {pseudoLoading ? <CircularProgress size={22} /> : 'Valider le pseudo'}
                </Button>
              </Box>
            </form>
          </Paper>
        )}

        {/* Infos compte */}
        <Paper elevation={4} sx={sectionPaper}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
            <ManageAccounts sx={{ color: '#B67A3C' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#e0c8ff' }}>Informations du compte</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <InfoRow label="Nom d'utilisateur" value={profile?.account || user?.username} />
            <InfoRow label="Email" value={profile?.email || user?.email} />
            <InfoRow label="Pseudo en jeu" value={profile?.pseudo || '—'} />
            <InfoRow label="Ogrines" value={profile ? `${profile.ogrines.toLocaleString()} ✨` : '—'} />
            <InfoRow label="Inscrit le" value={formatDate(profile?.dateRegister)} icon={<CalendarToday sx={{ fontSize: 16, color: '#B67A3C' }} />} />
            <InfoRow label="Dernière connexion" value={formatDate(profile?.lastConnectionDate)} />
          </Box>
        </Paper>

        {/* Inventaire de gains envoyables en jeu */}
        <Paper elevation={4} sx={sectionPaper}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1.5 }}>
            <ManageAccounts sx={{ color: '#B67A3C' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#e0c8ff' }}>
              Inventaire des gains (Roulette + Coffre temporel)
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', marginBottom: 2 }}>
            Tous vos gains sont affiches ici.
          </Typography>

          <Alert severity="info" sx={{ marginBottom: 2, borderRadius: 2 }}>
            L'envoi de l'inventaire est actif. Un seul clic suffit, inutile de spammer les boutons.
          </Alert>

          <Box sx={{ marginBottom: 2 }}>
            <label style={{ display: 'block', fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>
              Personnage destinataire
            </label>
            <select
              value={selectedDeliveryPlayerId}
              onChange={(e) => setSelectedDeliveryPlayerId(e.target.value)}
              disabled={!DELIVERY_BRIDGE_ENABLED || deliveryLoading || Boolean(sendingDeliveryId)}
              style={{
                width: '100%',
                padding: '9px 10px',
                borderRadius: 8,
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
                border: '1px solid rgba(182,122,60,0.35)',
              }}
            >
              <option value="" style={{ color: '#111', background: '#fff' }}>Personnage courant en jeu</option>
              {deliveryCharacters.map((c) => (
                <option key={c.id} value={String(c.id)} style={{ color: '#111', background: '#fff' }}>
                  {c.name} (ID {c.id}) {c.online ? '• en ligne' : ''}
                </option>
              ))}
            </select>

            {!canSendDeliveryToGame && (
              <Typography variant="caption" sx={{ color: '#ffcc80', display: 'block', marginTop: 0.8 }}>
                Aucun personnage en ligne selectionne: les envois sont temporairement bloques.
              </Typography>
            )}
          </Box>

          {deliveryMsg && (
            <Alert severity={deliveryMsg.type} sx={{ marginBottom: 2, borderRadius: 2 }}>
              {deliveryMsg.text}
            </Alert>
          )}

          {deliveryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', paddingY: 2 }}>
              <CircularProgress size={26} sx={{ color: '#B67A3C' }} />
            </Box>
          ) : deliveryInventory.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>
              Aucun gain disponible pour le moment.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
                gap: 1.25,
              }}
            >
              {deliveryInventory.map((row) => {
                const isDelivered = String(row.status || '').toUpperCase() === 'DELIVERED';
                const isSending = Number(sendingDeliveryId) === Number(row.id);
                return (
                  <Box
                    key={row.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'stretch',
                      minHeight: 152,
                      gap: 1.2,
                      padding: '12px 10px',
                      borderRadius: 2,
                      border: '1px solid rgba(182,122,60,0.2)',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
                    }}
                  >
                    <Box sx={{ minHeight: 78 }}>
                      <Typography variant="body2" sx={{ color: '#e0c8ff', fontWeight: 700, lineHeight: 1.2 }}>
                        {getCanonicalRewardName(row.itemName || row.item_name)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', marginTop: 0.6 }}>
                        {row.deliveryKind === 'TITLE' ? 'Titre' : 'Item'} • x{row.quantity} • {isDelivered ? 'Envoye' : 'En attente'}
                      </Typography>
                    </Box>

                    <Button
                      size="small"
                      variant={isDelivered ? 'outlined' : 'contained'}
                      onClick={() => sendDeliveryToGame(row.id)}
                      disabled={!canSendDeliveryToGame || isDelivered || isSending}
                      sx={isDelivered
                        ? {
                            borderColor: 'rgba(160,160,160,0.35)',
                            color: 'rgba(220,220,220,0.7)',
                          }
                        : {
                            background: 'linear-gradient(135deg, #7b1fa2 0%, #b039f7 55%, #e040fb 100%)',
                            color: '#fff',
                            fontWeight: 700,
                            border: '1px solid rgba(224, 64, 251, 0.45)',
                            boxShadow: '0 6px 16px rgba(176, 57, 247, 0.35)',
                            transition: 'transform 0.14s ease, box-shadow 0.14s ease, filter 0.14s ease',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #6a1b9a 0%, #9c27b0 45%, #d500f9 100%)',
                              boxShadow: '0 10px 22px rgba(213, 0, 249, 0.45)',
                              transform: 'translateY(-1px) scale(1.02)',
                              filter: 'saturate(1.12) brightness(1.08)',
                            },
                          }}
                    >
                      {isDelivered ? 'Envoye' : (isSending ? 'Envoi...' : 'Envoyer en jeu')}
                    </Button>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>

        {/* Stats votes */}
        {profile && (
          <Paper elevation={4} sx={sectionPaper}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
              <HowToVote sx={{ color: '#B67A3C' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#e0c8ff' }}>Statistiques de votes</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, marginBottom: 3 }}>
              <StatBox value={profile.monthly_votes} label="votes ce mois" icon="🗳️" />
              <StatBox value={profile.totalVotes} label="votes au total" icon="⭐" />
              <StatBox value={`${profile.ogrines.toLocaleString()}`} label="ogrines en jeu" icon="✨" />
            </Box>
            <Divider sx={{ borderColor: 'rgba(182,122,60,0.2)', marginBottom: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1 }}>
              <Chip
                label={`${tier.emoji} ${tier.name}`}
                sx={{ background: 'rgba(182,122,60,0.2)', color: '#e0c8ff', fontWeight: 700, fontSize: '0.9rem', border: '1px solid rgba(182,122,60,0.3)' }}
              />
              {nextTier && (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  Prochain : {nextTier.emoji} {nextTier.name} ({nextTier.min - profile.monthly_votes} votes)
                </Typography>
              )}
              {!nextTier && (
                <Typography variant="body2" sx={{ color: '#C9CED6', fontWeight: 600 }}>
                  👑 Palier maximum atteint !
                </Typography>
              )}
            </Box>
            {nextTier && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ height: 8, borderRadius: 4, background: 'rgba(182,122,60,0.15)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #B67A3C, #E040FB)' } }}
                />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.5 }}>
                  {progress}% vers {nextTier.name}
                </Typography>
              </Box>
            )}
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', display: 'block', marginTop: 1 }}>
              Les paliers sont réinitialisés au début de chaque mois.
            </Typography>
          </Paper>
        )}

        {/* Changer mot de passe */}
        <Accordion sx={{ background: 'rgba(26,10,46,0.7)', border: '1px solid rgba(182,122,60,0.2)', borderRadius: '16px !important', marginBottom: 2, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#B67A3C' }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Lock sx={{ color: '#B67A3C' }} />
              <Typography sx={{ fontWeight: 600, color: '#e0c8ff' }}>Changer le mot de passe</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {passMsg && <Alert severity={passMsg.type} sx={{ marginBottom: 2, borderRadius: 2 }}>{passMsg.text}</Alert>}
            <form onSubmit={submitPassword}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Mot de passe actuel" name="current_password" type="password" required value={passForm.current_password} onChange={handlePassChange} size="small" InputProps={{ sx: fieldSx }} />
                <TextField label="Nouveau mot de passe" name="new_password" type="password" required value={passForm.new_password} onChange={handlePassChange} size="small" InputProps={{ sx: fieldSx }} />
                <TextField label="Confirmer le nouveau mot de passe" name="confirm_password" type="password" required value={passForm.confirm_password} onChange={handlePassChange} size="small" InputProps={{ sx: fieldSx }} />
                {profile?.secret_question && (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
                    🔐 {profile.secret_question}
                  </Typography>
                )}
                <TextField label="Réponse secrète" name="secret_answer" required value={passForm.secret_answer} onChange={handlePassChange} size="small" InputProps={{ sx: fieldSx }} />
                <Button type="submit" variant="contained" disabled={passLoading} sx={btnSx}>
                  {passLoading ? <CircularProgress size={22} /> : 'Modifier le mot de passe'}
                </Button>
              </Box>
            </form>
          </AccordionDetails>
        </Accordion>

        {/* Changer email */}
        <Accordion sx={{ background: 'rgba(26,10,46,0.7)', border: '1px solid rgba(182,122,60,0.2)', borderRadius: '16px !important', marginBottom: 2, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#B67A3C' }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Email sx={{ color: '#B67A3C' }} />
              <Typography sx={{ fontWeight: 600, color: '#e0c8ff' }}>Changer l'adresse email</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {emailMsg && <Alert severity={emailMsg.type} sx={{ marginBottom: 2, borderRadius: 2 }}>{emailMsg.text}</Alert>}
            <form onSubmit={submitEmail}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Nouvelle adresse email" name="new_email" type="email" required value={emailForm.new_email} onChange={handleEmailChange} size="small" InputProps={{ sx: fieldSx }} />
                <TextField label="Mot de passe de confirmation" name="password" type="password" required value={emailForm.password} onChange={handleEmailChange} size="small" InputProps={{ sx: fieldSx }} />
                {profile?.secret_question && (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
                    🔐 {profile.secret_question}
                  </Typography>
                )}
                <TextField label="Réponse secrète" name="secret_answer" required value={emailForm.secret_answer} onChange={handleEmailChange} size="small" InputProps={{ sx: fieldSx }} />
                <Button type="submit" variant="contained" disabled={emailLoading} sx={btnSx}>
                  {emailLoading ? <CircularProgress size={22} /> : "Modifier l'email"}
                </Button>
              </Box>
            </form>
          </AccordionDetails>
        </Accordion>

        {/* IPs de confiance (Whitelist) */}
        <Accordion
          sx={{ background: 'rgba(26,10,46,0.7)', border: '1px solid rgba(182,122,60,0.2)', borderRadius: '16px !important', '&:before': { display: 'none' } }}
          onChange={(_, expanded) => { if (expanded) loadWhitelistIps(); }}
        >
          <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#B67A3C' }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Security sx={{ color: '#B67A3C' }} />
              <Typography sx={{ fontWeight: 600, color: '#e0c8ff' }}>IPs de confiance</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', marginBottom: 2 }}>
              Ces adresses IP ont été validées par email. Supprimez une IP si vous ne l'utilisez plus ou si elle ne vous appartient pas.
            </Typography>
            {/* Bouton vérification IP courante */}
            <Box sx={{ marginBottom: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRequestIpValidation}
                disabled={ipReqLoading}
                startIcon={ipReqLoading ? <CircularProgress size={14} sx={{ color: '#B67A3C' }} /> : <Security sx={{ fontSize: 16 }} />}
                sx={{
                  borderColor: 'rgba(182,122,60,0.5)', color: '#d5a777', borderRadius: 2,
                  '&:hover': { borderColor: '#B67A3C', background: 'rgba(182,122,60,0.1)' },
                  '&:disabled': { borderColor: 'rgba(182,122,60,0.2)', color: 'rgba(255,255,255,0.3)' },
                }}
              >
                Vérifier mon IP pour accéder au jeu
              </Button>
              {ipReqMsg && (
                <Alert severity={ipReqMsg.type} sx={{ marginTop: 1, borderRadius: 2, fontSize: '0.82rem' }}>
                  {ipReqMsg.text}
                </Alert>
              )}
            </Box>
            {whitelistMsg && <Alert severity={whitelistMsg.type} sx={{ marginBottom: 2, borderRadius: 2 }}>{whitelistMsg.text}</Alert>}
            {whitelistLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                <CircularProgress size={28} sx={{ color: '#B67A3C' }} />
              </Box>
            ) : whitelistIps.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', textAlign: 'center', padding: 2 }}>
                Aucune IP de confiance enregistrée.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {whitelistIps.map((entry) => (
                  <Box
                    key={entry.id}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: 'rgba(182,122,60,0.08)', border: '1px solid rgba(182,122,60,0.15)',
                      borderRadius: 2, padding: '10px 14px',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ color: '#e0c8ff', fontWeight: 600, fontFamily: 'monospace' }}>
                        {entry.ip}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                        Validée le {entry.validated_at ? new Date(entry.validated_at).toLocaleDateString('fr-FR') : '—'}
                      </Typography>
                    </Box>
                    <Tooltip title="Supprimer cette IP">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteIp(entry.id)}
                        sx={{ color: 'rgba(244,67,54,0.7)', '&:hover': { color: '#f44336', background: 'rgba(244,67,54,0.1)' } }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

      </Container>
    </Box>
  );
}

function InfoRow({ label, value, icon }) {
  return (
    <Box sx={{ background: 'rgba(255,255,255,0.03)', borderRadius: 2, padding: '10px 14px', border: '1px solid rgba(182,122,60,0.1)' }}>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: 0.3 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {icon}
        <Typography variant="body2" sx={{ color: '#e0c8ff', fontWeight: 500 }}>{value || '—'}</Typography>
      </Box>
    </Box>
  );
}

function StatBox({ value, label, icon }) {
  return (
    <Box sx={{ textAlign: 'center', background: 'rgba(182,122,60,0.08)', borderRadius: 2, padding: 2, border: '1px solid rgba(182,122,60,0.15)' }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color: '#e0c8ff' }}>{icon} {value}</Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>{label}</Typography>
    </Box>
  );
}

// Gère les formats: "2025~03~07~21~31", "2025-03-07", "01/01/2015", etc.
function formatDate(value) {
  if (!value) return '—';
  if (typeof value === 'string' && value.includes('~')) {
    const p = value.split('~');
    const date = p.length >= 3 ? `${p[2]}/${p[1]}/${p[0]}` : value;
    const time = p.length >= 5 ? ` ${p[3]}:${p[4]}` : '';
    return date + time;
  }
  // Format "DD/MM/YYYY" → déjà bon
  if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('fr-FR');
}
